import { Call } from '../../model/call';
import { Message } from '../../model/message';

export type CallLogIconKind =
  | 'ongoing'
  | 'answered'
  | 'voicemail'
  | 'missed'
  | 'error'
  | 'unknown';

export type MessageLogIconKind = 'sent' | 'error';

export interface CallLogIconView {
  kind: CallLogIconKind;
  icon: string;
  /** Material theme palette name or custom token key */
  color: 'primary' | 'warn' | 'success' | 'muted';
  tooltip: string;
}

export interface MessageLogIconView {
  kind: MessageLogIconKind;
  icon: string;
  color: 'success' | 'warn';
  tooltip: string;
}

function buildCallTooltip(call: Call): string {
  const parts = [`Twilio status: ${call.status || 'unknown'}`];
  if (call.answeredBy) {
    parts.push(`answered by: ${call.answeredBy}`);
  }
  return parts.join(', ');
}

export function getCallLogIcon(call: Call): CallLogIconView {
  const status = (call.status || '').toLowerCase();
  const answeredBy = (call.answeredBy || '').toLowerCase();
  const tooltip = buildCallTooltip(call);

  if (status === 'failed') {
    return { kind: 'error', icon: 'error_outline', color: 'warn', tooltip };
  }

  if (['queued', 'ringing', 'initiated', 'in-progress', 'in_progress'].includes(status)) {
    return { kind: 'ongoing', icon: 'phone_in_talk', color: 'primary', tooltip };
  }

  if (status === 'completed') {
    if (answeredBy === 'human' || answeredBy === 'unknown') {
      return { kind: 'answered', icon: 'call', color: 'success', tooltip };
    }
    if (answeredBy.startsWith('machine')) {
      return { kind: 'voicemail', icon: 'voicemail', color: 'muted', tooltip };
    }
    return { kind: 'missed', icon: 'call_missed_outgoing', color: 'warn', tooltip };
  }

  if (['no-answer', 'noanswer', 'busy', 'canceled', 'cancelled'].includes(status)) {
    return { kind: 'missed', icon: 'call_missed_outgoing', color: 'warn', tooltip };
  }

  return { kind: 'unknown', icon: 'help_outline', color: 'warn', tooltip };
}

export function isAnsweredCall(call: Call): boolean {
  return getCallLogIcon(call).kind === 'answered';
}

export function getMessageLogIcon(message: Message): MessageLogIconView {
  const status = (message.status || '').toLowerCase();
  const tooltip = `Twilio status: ${message.status || 'unknown'}`;

  if (status === 'undelivered' || status === 'failed') {
    return { kind: 'error', icon: 'error_outline', color: 'warn', tooltip };
  }

  return { kind: 'sent', icon: 'check', color: 'success', tooltip };
}
